import { ApiClient } from "@taxi-driver/shared/src/services/api-service";
import { AuthLoginReq } from "@taxi-driver/shared/src/auth";
import { DriverEntity } from "@taxi-driver/shared/src/driver/driver-entity";
import { getToken } from "./utils/store";
import { ReqFormDriver } from "@taxi-driver/shared/src/driver/driver-dto";
import { RateEntity } from "@taxi-driver/shared/src/rate/rate-entity";
import {
  ReqUpdateRate,
  ReqCreateRate,
} from "@taxi-driver/shared/src/rate/rate-dto";
import {
  DistanceRequest,
  DistanceResponse,
} from "@taxi-driver/shared/src/distance";

const api = new ApiClient(import.meta.env.VITE_API_URL);

export async function serviceLogin(data: AuthLoginReq): Promise<any> {
  return api.post("/api/auth/login", data);
}

export async function getDrivers(): Promise<DriverEntity[]> {
  return await api.get(
    "/api/driver",
    {},
    { headers: { Authorization: `Bearer ${getToken()}` } },
  );
}

export async function createDriver(data: ReqFormDriver): Promise<DriverEntity> {
  return await api.post("/api/driver", data, {
    headers: { Authorization: `Bearer ${getToken()}` },
  });
}

export async function updateDriver(
  data: ReqFormDriver,
  id: number,
): Promise<DriverEntity> {
  return await api.put(`/api/driver/${id}`, data, {
    headers: { Authorization: `Bearer ${getToken()}` },
  });
}

export async function deleteDriver(id: number): Promise<DriverEntity> {
  return await api.delete(
    `/api/driver/${id}`,
    {},
    { headers: { Authorization: `Bearer ${getToken()}` } },
  );
}
export async function updateDriverPosition(
  id: number,
  isEnd: boolean,
): Promise<DriverEntity> {
  return await api.post(
    `/api/driver/${id}/position`,
    { isEnd },
    { headers: { Authorization: `Bearer ${getToken()}` } },
  );
}

export async function getRates(): Promise<RateEntity[]> {
  return await api.get(
    `/api/rates`,
    {},
    { headers: { Authorization: `Bearer ${getToken()}` } },
  );
}

export async function createRate(data: ReqCreateRate): Promise<RateEntity> {
  return await api.post(`/api/rates`, data, {
    headers: { Authorization: `Bearer ${getToken()}` },
  });
}

export async function updateRate(
  id: number,
  data: ReqUpdateRate,
): Promise<RateEntity> {
  return await api.put(`/api/rates/${id}`, data, {
    headers: { Authorization: `Bearer ${getToken()}` },
  });
}

export async function deleteRate(id: number): Promise<RateEntity> {
  return await api.delete(
    `/api/rates/${id}`,
    {},
    {
      headers: { Authorization: `Bearer ${getToken()}` },
    },
  );
}

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export interface ChangePasswordResponse {
  success: boolean;
  message: string;
}

export async function changePassword(
  data: ChangePasswordRequest,
): Promise<ChangePasswordResponse> {
  return await api.put("/api/auth/change-password", data, {
    headers: { Authorization: `Bearer ${getToken()}` },
  });
}

// ========== LOCATION API ==========

export interface LocationData {
  driverId: number;
  latitude: number;
  longitude: number;
  timestamp: string;
  accuracy?: number;
  altitude?: number;
  velocity?: number;
  battery?: number;
  updatedAt: Date;
}

/**
 * Obtiene todas las ubicaciones en memoria de todos los conductores
 */
export async function getAllLocations(): Promise<LocationData[]> {
  return await api.get(
    "/api/driver/locations",
    {},
    {
      headers: { Authorization: `Bearer ${getToken()}` },
    },
  );
}

/**
 * Obtiene la última ubicación de todos los conductores
 */
export async function getLatestLocations(): Promise<LocationData[]> {
  return await api.get(
    "/api/driver/locations/latest",
    {},
    {
      headers: { Authorization: `Bearer ${getToken()}` },
    },
  );
}

/**
 * Interfaz para ubicación con datos del conductor incluidos
 */
export interface DriverLocationWithData extends LocationData {
  driver: {
    id: number;
    driver: string;
    plate: string;
    number: number;
    status: string;
  } | null;
}

/**
 * Obtiene la última ubicación de todos los conductores con datos del conductor
 */
export async function getLatestLocationsWithDrivers(): Promise<
  DriverLocationWithData[]
> {
  return await api.get(
    "/api/driver/locations/latest",
    {},
    {
      headers: { Authorization: `Bearer ${getToken()}` },
    },
  );
}

/**
 * Obtiene todas las ubicaciones de un conductor específico
 */
export async function getDriverLocations(
  driverId: number,
): Promise<LocationData[]> {
  return await api.get(
    `/api/driver/${driverId}/locations`,
    {},
    {
      headers: { Authorization: `Bearer ${getToken()}` },
    },
  );
}

/**
 * Obtiene la última ubicación de un conductor específico
 */
export async function getDriverLatestLocation(
  driverId: number,
): Promise<LocationData> {
  return await api.get(
    `/api/driver/${driverId}/location/latest`,
    {},
    {
      headers: { Authorization: `Bearer ${getToken()}` },
    },
  );
}

// ========== TRACKING URL API ==========

export interface TrackingUrlResponse {
  driverId: number;
  driverName: string;
  trackingUrl: string;
  instructions: string;
}

/**
 * Obtiene la URL de configuración de OwnTracks para un conductor específico
 */
export async function getDriverTrackingUrl(
  driverId: number,
): Promise<TrackingUrlResponse> {
  return await api.get(`/api/driver/${driverId}/tracking-url`);
}

export async function calculateDistance(
  origin: { lat: number; lng: number },
  destination: { lat: number; lng: number },
): Promise<DistanceResponse> {
  const requestData: DistanceRequest = {
    origin,
    destination,
  };

  return await api.post("/api/distance/calculate", requestData);
}

// ========== SYSTEM CONFIG API ==========

export interface SystemConfigData {
  id: number;
  commissionPercent: number;
  frequency: number;
  inputTemplate: string | null;
  outputTemplate: string | null;
  whatsappHeader: string | null;
  aiApiKey: string | null;
  aiPrompt: string | null;
  aiModel: string | null;
  aiEnabled: boolean;
  whatsappAutoTemplate: string | null;
  whatsappAutoMessageEnabled: boolean;
}

export async function getSystemConfig(): Promise<SystemConfigData> {
  return await api.get(
    "/api/system-config",
    {},
    { headers: { Authorization: `Bearer ${getToken()}` } },
  );
}

export async function updateSystemConfig(
  data: Partial<
    Pick<
      SystemConfigData,
      | "commissionPercent"
      | "frequency"
      | "inputTemplate"
      | "outputTemplate"
      | "whatsappHeader"
      | "whatsappAutoTemplate"
      | "whatsappAutoMessageEnabled"
      | "aiPrompt"
      | "aiEnabled"
    >
  >,
): Promise<SystemConfigData> {
  return await api.put("/api/system-config", data, {
    headers: { Authorization: `Bearer ${getToken()}` },
  });
}

export async function testAi(): Promise<{
  success: boolean;
  error?: string;
  data?: any;
}> {
  return await api.post(
    "/api/system-config/test-ai",
    {},
    { headers: { Authorization: `Bearer ${getToken()}` } },
  );
}

// ========== WEBHOOK LOG API ==========

export interface WebhookRequestLogData {
  id: number;
  created_at: string;
  success: boolean;
  statusCode?: number | null;
  parsedWith?: string | null;
  aiProcessed: boolean;
  aiSuccess?: boolean | null;
  tripId?: number | null;
  requestBody: string;
  bodySize?: number | null;
  messagePreview?: string | null;
  contactName?: string | null;
  phoneNumber?: string | null;
  messageId?: string | null;
  webhookId?: string | null;
  webhookName?: string | null;
  responseTimeMs?: number | null;
  errorMessage?: string | null;
  aiResponse?: string | null;
  aiExtractedData?: string | null;
  parsedData?: string | null;
  priceWarning?: string | null;
  trip?: {
    id: number;
    customerName?: string | null;
    origin: string;
    destination: string;
    created_at: string;
  } | null;
}

export async function getWebhookLogs(filters?: {
  success?: boolean;
  aiProcessed?: boolean;
  q?: string;
  limit?: number;
}): Promise<WebhookRequestLogData[]> {
  const query: Record<string, string> = {};
  if (filters?.success !== undefined) query.success = String(filters.success);
  if (filters?.aiProcessed !== undefined)
    query.aiProcessed = String(filters.aiProcessed);
  if (filters?.q?.trim()) query.q = filters.q.trim();
  if (filters?.limit) query.limit = String(filters.limit);

  const queryString = new URLSearchParams(query).toString();
  const url = `/api/webhook-logs${queryString ? `?${queryString}` : ""}`;

  return await api.get(
    url,
    {},
    { headers: { Authorization: `Bearer ${getToken()}` } },
  );
}

// ========== TRIP API ==========

export interface TripData {
  id: number;
  driverId: number | null;
  datetime: string | null;
  origin: string;
  destination: string;
  isAirport: boolean;
  grossPrice: number;
  netPrice: number;
  passengers?: number | null;
  suitcases?: number | null;
  customerName?: string | null;
  customerPhone?: string | null;
  flightNumber?: string | null;
  direction?: string | null;
  isNational?: boolean;
  isConvenio: boolean;
  priceWarning?: string | null;
  created_at: string;
  updated_at: string;
  driver: {
    id: number;
    number: number;
    plate: string;
    driver: string;
  } | null;
}

export interface TripStats {
  totalTrips: number;
  totalGross: number;
  totalNet: number;
}

export interface CreateTripRequest {
  driverId?: number | null;
  datetime?: string;
  origin: string;
  destination: string;
  isAirport?: boolean;
  grossPrice: number;
  netPrice: number;
  passengers?: number;
  suitcases?: number;
  customerName?: string;
  customerPhone?: string;
  flightNumber?: string;
  isNational?: boolean;
  direction?: string;
}

export interface UpdateTripRequest {
  driverId?: number | null;
  datetime?: string;
  origin?: string;
  destination?: string;
  isAirport?: boolean;
  grossPrice?: number;
  netPrice?: number;
  passengers?: number;
  suitcases?: number;
  customerName?: string;
  customerPhone?: string;
  flightNumber?: string;
  direction?: string;
  isNational?: boolean;
}

export async function createTrip(data: CreateTripRequest): Promise<any> {
  return await api.post("/api/trips", data, {
    headers: { Authorization: `Bearer ${getToken()}` },
  });
}

export async function updateTrip(
  id: number,
  data: UpdateTripRequest,
): Promise<any> {
  return await api.put(`/api/trips/${id}`, data, {
    headers: { Authorization: `Bearer ${getToken()}` },
  });
}

export async function toggleConvenio(
  id: number,
  isConvenio: boolean,
): Promise<any> {
  return await api.put(
    `/api/trips/${id}`,
    { isConvenio },
    { headers: { Authorization: `Bearer ${getToken()}` } },
  );
}

export async function toggleNational(
  id: number,
  isNational: boolean,
): Promise<any> {
  return await api.put(
    `/api/trips/${id}`,
    { isNational },
    { headers: { Authorization: `Bearer ${getToken()}` } },
  );
}

export async function deleteTrip(id: number): Promise<any> {
  return await api.delete(
    `/api/trips/${id}`,
    {},
    { headers: { Authorization: `Bearer ${getToken()}` } },
  );
}

export async function assignDriverToTrip(
  tripId: number,
  driverId: number,
  isNational?: boolean,
): Promise<any> {
  return await api.patch(
    `/api/trips/${tripId}/assign-driver`,
    { driverId, ...(isNational !== undefined ? { isNational } : {}) },
    { headers: { Authorization: `Bearer ${getToken()}` } },
  );
}

export async function updateTripDatetime(
  tripId: number,
  datetime: string | null,
): Promise<any> {
  return await api.patch(
    `/api/trips/${tripId}/datetime`,
    { datetime },
    { headers: { Authorization: `Bearer ${getToken()}` } },
  );
}

export async function getTrips(params?: {
  startDate?: string;
  endDate?: string;
  driverId?: number;
  isAirport?: boolean;
  noDriver?: boolean;
  withDriver?: boolean;
  searchTerm?: string;
  noDate?: boolean;
  withDate?: boolean;
}): Promise<TripData[]> {
  const query: Record<string, string> = {};
  if (params?.startDate) query.startDate = params.startDate;
  if (params?.endDate) query.endDate = params.endDate;
  if (params?.driverId) query.driverId = String(params.driverId);
  if (params?.isAirport !== undefined)
    query.isAirport = String(params.isAirport);
  if (params?.noDriver) query.noDriver = "true";
  if (params?.withDriver) query.withDriver = "true";
  if (params?.searchTerm) query.searchTerm = params.searchTerm;
  if (params?.noDate) query.noDate = "true";
  if (params?.withDate) query.withDate = "true";

  const queryString = new URLSearchParams(query).toString();
  const url = `/api/trips${queryString ? `?${queryString}` : ""}`;

  return await api.get(
    url,
    {},
    {
      headers: { Authorization: `Bearer ${getToken()}` },
    },
  );
}

export async function getTripStats(params?: {
  startDate?: string;
  endDate?: string;
  driverId?: number;
  isAirport?: boolean;
  noDriver?: boolean;
  withDriver?: boolean;
  searchTerm?: string;
  noDate?: boolean;
  withDate?: boolean;
}): Promise<TripStats> {
  const query: Record<string, string> = {};
  if (params?.startDate) query.startDate = params.startDate;
  if (params?.endDate) query.endDate = params.endDate;
  if (params?.driverId) query.driverId = String(params.driverId);
  if (params?.isAirport !== undefined)
    query.isAirport = String(params.isAirport);
  if (params?.noDriver) query.noDriver = "true";
  if (params?.withDriver) query.withDriver = "true";
  if (params?.searchTerm) query.searchTerm = params.searchTerm;
  if (params?.noDate) query.noDate = "true";
  if (params?.withDate) query.withDate = "true";

  const queryString = new URLSearchParams(query).toString();
  const url = `/api/trips/stats${queryString ? `?${queryString}` : ""}`;

  return await api.get(
    url,
    {},
    {
      headers: { Authorization: `Bearer ${getToken()}` },
    },
  );
}

// ========== CLIPBOARD LOG API ==========

export interface CreateClipboardLogRequest {
  tripId?: number;
  success: boolean;
  strategy?: string | null;
  strategiesAttempted?: string;
  textLength?: number;
  textPreview?: string;
  userAgent?: string;
  isSecureContext?: boolean;
  isIOS?: boolean;
  errorMessage?: string;
  expectedCustomerName?: string;
  expectedOrigin?: string;
  expectedDestination?: string;
}

export async function createClipboardLog(
  data: CreateClipboardLogRequest,
): Promise<any> {
  return await api.post("/api/clipboard-logs", data, {
    headers: { Authorization: `Bearer ${getToken()}` },
  });
}
